import React, { useState } from "react";

import { Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Icons as GridIcons,
  RowActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError, AxiosResponse } from "axios";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiCopy, FiEdit } from "react-icons/fi";
import { Badge } from "reactstrap";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { LanguageProvider } from "../../../../../../../context/LanguageContext";
import { useAppState } from "../../../../../../../context/StateContext";
import { useLanguageHook } from "../../../../../../../hooks/useLanguageHook";
import { Error } from "../../../../../../../models/error";
import { LanguageTranslateValues } from "../../../../../../../models/language.translate.values";
import { Languages as LanguagesType } from "../../../../../../../models/languages";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../models/shipeezi.table";
import LanguagesService from "../../../../../../../services/languages.service";
import { checkError } from "../../../../../../../util/checkError";
import { Status } from "../../../../../../../util/enums";
import LanguageModal from "./LanguageModal";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

export type UpdateRecord = {
  language: LanguagesType;
  languageTranslateToUpdate?: LanguageTranslateValues[];
};

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const Languages = ({ permission }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const { setLanguage, updatedTranslate, setUpdatedTranslate } = useLanguageHook();

  const [languages, setLanguages] = useState<LanguagesType[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const resetValuesAndCloseModal = () => {
    setHasError(false);
    setOpenModal(false);
    setLanguage(undefined);
    setUpdatedTranslate([]);
  };

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
    `}
    >
      {status}
    </Badge>
  );

  const { refetch, isFetching } = useQuery(
    ["languages.list", page, pageSize, filters, searches, sorting],
    () =>
      LanguagesService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response: AxiosResponse<LanguagesType[]>) => {
        if (response && response.data) setLanguages(response.data);
      },
    }
  );

  const { refetch: refetchLanguageTotal } = useQuery(
    ["languages.total", filters, searches],
    () =>
      LanguagesService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: updateLanguage, isLoading: isUpdating } = useMutation(
    ["language.update"],
    ({ id, language }: { id: number; language: UpdateRecord }) =>
      LanguagesService.update(id, language),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        resetValuesAndCloseModal();
        refetch();
        refetchLanguageTotal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        setHasError(true);
      },
    }
  );

  const { mutate: createLanguage, isLoading: isCreating } = useMutation(
    ["language.create"],
    (language: LanguagesType) =>
      LanguagesService.create({
        ...language,
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        resetValuesAndCloseModal();
        refetch();
        refetchLanguageTotal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error) checkError(error, translate("anErrorOccurredTryAgain"));
        setHasError(true);
      },
    }
  );

  const handleRenderEditButton = (index: number) => {
    const lang = languages?.find((language) => Number(language?.id) === Number(index));

    if (lang) {
      if (lang?.companyId) {
        return (
          <Tooltip title={translate("edit") || ""} placement="top">
            <div>
              <FiEdit
                size={18}
                color="#475467"
                className={classes.iconButton}
                onClick={() => {
                  if (hasError !== undefined) setHasError(undefined);
                  setLanguage(lang);
                  setOpenModal(true);
                }}
              />
            </div>
          </Tooltip>
        );
      }
    }
  };

  const handleRenderChangeStatusButton = (index: number) => {
    const lang = languages?.find((language) => Number(language?.id) === Number(index));

    if (lang) {
      if (lang?.companyId) {
        return (
          <Tooltip title={translate("changeStatus") || ""} placement="top">
            <div>
              <GridIcons.status
                color="#475467"
                className={classes.iconButton}
                onClick={() => {
                  updateLanguage({
                    id: Number(index),
                    language: {
                      language: {
                        ...lang,
                        status: lang.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
                      },
                      languageTranslateToUpdate: updatedTranslate,
                    },
                  });
                }}
              />
            </div>
          </Tooltip>
        );
      }
    }
  };

  const handleRenderDuplicate = (index: number) => {
    const lang: LanguagesType | undefined = languages?.find(
      (language) => Number(language?.id) === Number(index)
    );

    if (lang) {
      return (
        <Tooltip title={translate("duplicate") || ""} placement="top">
          <div>
            <FiCopy
              size={18}
              color="#475467"
              className={classes.iconButton}
              onClick={() => {
                const copyLanguage = _.cloneDeep(lang);
                const baseLanguageId = copyLanguage?.id;

                delete copyLanguage?.id;
                copyLanguage.name = `${copyLanguage.name} - copy`;
                createLanguage({ ...copyLanguage, baseLanguageId });
              }}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const rowActions: RowActions = {
    orderBy: ["edit", "changeStatus", "duplicate"],
  };

  const hanleRowActionsExtraByPermission = () => {
    let actions:RowActionsExtra[] = [];

    if (permission && isShowAction(currentUser, permission.edit)) {
      actions?.push({
        title: "Edit",
        key: "edit",
        buttonAndAction: handleRenderEditButton,
      })
    }

    actions?.push({
      title: "Change Status",
      key: "changeStatus",
      buttonAndAction: handleRenderChangeStatusButton,
    },
    {
      title: "Duplicate Language",
      key: "duplicate",
      buttonAndAction: handleRenderDuplicate,
    })

    return actions;
  }

  const rowActionsExtra: RowActionsExtra[] = hanleRowActionsExtraByPermission();

  return (
    <div>
      <ShipeeziNewGrid
        data={languages}
        columns={[
          { name: "prefix", title: "Prefix" },
          { name: "name", title: "Language" },
          {
            name: "flag",
            title: "Flag",
            hideSearch: true,
            hideSort: true,
            formatComponent: (flag: string) => (
              <img
                loading="lazy"
                width="40"
                src={flag ? `https://flagcdn.com/w80/${flag.toLowerCase()}.png` : ""}
                srcSet={flag ? `https://flagcdn.com/w160/${flag.toLowerCase()}.png 2x` : ""}
                alt=""
              />
            ),
          },
          { name: "usage", title: "Usage" },
          {
            name: "status",
            title: "Status",
            hideSearch: true,
            formatComponent: (status: string) => badgeStatus(status),
          },
        ]}
        totalItems={total}
        loadingData={isFetching}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={rowActions}
        rowActionsExtra={rowActionsExtra}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleNewRecord"],
          },
          handleNewRecord: permission && isShowAction(currentUser, permission.create)
            ? {
              text: translate("create"),
              action: () => {
                if (hasError !== undefined) setHasError(undefined);
                setLanguage(undefined);
                setOpenModal(true);
              },
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: handleSearch,
          },
          handleFilterBy: {
            categories: [
              { name: "Status", options: ["Active", "Inactive"] },
              { name: "Where", column: "usage" },
              { name: "Language", column: "name" },
              { name: "Flag", column: "flag" },
              { name: "Prefix", column: "prefix" },
            ],
            onChange: handleFilter,
          },
        }}
      />
      {openModal && (
        <LanguageModal
          open={openModal}
          hasError={hasError}
          isLoading={isUpdating || isCreating}
          closeModal={() => resetValuesAndCloseModal()}
          updateLanguage={(language: LanguagesType) =>
            language.id &&
            updateLanguage({
              id: language.id,
              language: { language, languageTranslateToUpdate: updatedTranslate } as UpdateRecord,
            })
          }
          createLanguage={(language: LanguagesType) =>
            createLanguage({ ...language, companyId: currentUser?.companyId })
          }
        />
      )}
    </div>
  );
};

const LanguagePage = ({ permission }: Props) => {
  return (
    <LanguageProvider>
      <Languages permission={permission} />
    </LanguageProvider>
  );
};

export default LanguagePage;
